import { upload } from '../../../api/client';
import AvatarEditor, { AvatarEditorProps } from './AvatarEditor';
import { handleApiError, showErrorNotification } from '../../../utils/errorHandler';

export interface AvatarUploadControlProps
  extends Omit<AvatarEditorProps, 'onFileReady'> {
  /**
   * 头像上传成功后的回调：拿到后端返回的公开 URL，调用方自行持久化到业务字段
   * （例如：profile → updateProfile({avatar})；customer → PUT /customers/:id）。
   */
  onUploaded: (url: string) => Promise<void> | void;
  /** 最大文件大小（字节），默认 2MB */
  maxSize?: number;
}

/**
 * 项目级公用头像上传控件。
 *
 * 职责单一：上传头像 blob → 后端 POST /uploads/avatar → 拿到 URL → onUploaded(url)
 *
 * 与证件照上传（/manual-application/upload-id-card）和订单凭证上传（/uploads/payment-voucher）
 * 在路由、落盘目录、mime 白名单上均严格隔离，不可混用。
 */
export default function AvatarUploadControl({
  onUploaded,
  maxSize = 2 * 1024 * 1024,
  ...editorProps
}: AvatarUploadControlProps) {
  const handleFileReady = async (blob: Blob): Promise<string> => {
    if (blob.size > maxSize) {
      const mb = (maxSize / 1024 / 1024).toFixed(1);
      throw new Error(`头像文件大小不能超过 ${mb}MB`);
    }
    const ext = blob.type.split('/')[1] || 'png';
    const file = new File([blob], `avatar.${ext}`, { type: blob.type });

    try {
      const result = await upload<{ url: string }>('/uploads/avatar', file);
      if (!result?.url) {
        throw new Error('上传失败：服务器未返回文件地址');
      }
      await onUploaded(result.url);
      return result.url;
    } catch (error) {
      const info = handleApiError(error);
      showErrorNotification(info);
      throw error instanceof Error ? error : new Error(info.message);
    }
  };

  return <AvatarEditor {...editorProps} onFileReady={handleFileReady} />;
}
